import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { AlertCircle } from 'lucide-react';
import { setSessionToken } from '../utils/apiAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError({
        stage: 'missing_session_id',
        message: 'OAuth provider did not return a session_id in the URL fragment.',
        detail: `hash="${hash}"`,
      });
      return;
    }

    (async () => {
      let res;
      try {
        res = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });
      } catch (e) {
        setError({
          stage: 'network',
          message: 'Could not reach the backend to exchange the session.',
          detail: `${e.name}: ${e.message}`,
          url: `${API}/auth/session`,
        });
        return;
      }

      if (!res.ok) {
        let body = '';
        try { body = await res.text(); } catch { /* ignore */ }
        setError({
          stage: 'session_exchange',
          message: `Backend rejected the session exchange (HTTP ${res.status}).`,
          detail: body.slice(0, 400) || res.statusText,
          url: `${API}/auth/session`,
        });
        return;
      }

      // 200 OK — but we still need to verify the session cookie actually
      // got persisted (the most common failure mode on cross-domain prod
      // setups is the Set-Cookie being silently rejected by the browser).
      let userData;
      try {
        userData = await res.json();
      } catch (e) {
        setError({
          stage: 'parse_response',
          message: 'Could not parse the backend response.',
          detail: e.message,
        });
        return;
      }

      // Store the token in localStorage as a Bearer fallback. The fetch
      // interceptor (installFetchAuth) will attach it to every API call
      // automatically, which keeps us authenticated even when the browser
      // refuses to persist the cross-domain Set-Cookie (Safari iOS etc.).
      if (userData?.session_token) {
        setSessionToken(userData.session_token);
      }

      // Confirm the token actually round-trips: call /auth/me with credentials.
      try {
        const meRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (!meRes.ok) {
          setError({
            stage: 'cookie_not_persisted',
            message:
              `Logged in OK, but the session cookie was not accepted by the ` +
              `browser (auth/me returned ${meRes.status}). This usually means ` +
              `the API and the site are on different domains and the cookie ` +
              `is being blocked.`,
            detail: `API=${API}\nFrontend origin=${window.location.origin}`,
            url: `${API}/auth/me`,
          });
          return;
        }
      } catch (e) {
        setError({
          stage: 'cookie_verify_failed',
          message: 'Could not verify the session cookie after login.',
          detail: `${e.name}: ${e.message}`,
        });
        return;
      }

      setUser(userData);
      navigate('/dashboard', { replace: true, state: { user: userData } });
    })();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F5] px-4">
        <div
          className="bg-white border border-red-200 rounded-md p-6 max-w-xl w-full shadow-sm"
          data-testid="auth-callback-error"
        >
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h1
                className="text-lg font-semibold text-[#1A1D1C]"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                Sign-in didn't complete
              </h1>
              <p className="text-xs text-[#6B7A73] uppercase tracking-wider mt-0.5">
                {error.stage.replace(/_/g, ' ')}
              </p>
            </div>
          </div>
          <p className="text-sm text-[#1A1D1C] mb-3">{error.message}</p>
          {error.url && (
            <p className="text-xs text-[#6B7A73] mb-2">
              <span className="font-medium">URL:</span> {error.url}
            </p>
          )}
          {error.detail && (
            <pre
              className="text-xs bg-[#F5F6F5] border border-[#E6EBE8] rounded p-2 mb-4 whitespace-pre-wrap break-all"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              data-testid="auth-callback-detail"
            >
              {error.detail}
            </pre>
          )}
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/login', { replace: true })}
              className="bg-[#2D6B68] hover:bg-[#235552] text-white"
              data-testid="auth-callback-retry"
            >
              Back to sign in
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const dump = JSON.stringify(
                  { ...error, frontendOrigin: window.location.origin, api: API },
                  null,
                  2
                );
                navigator.clipboard?.writeText(dump);
              }}
              className="border-[#E6EBE8]"
              data-testid="auth-callback-copy"
            >
              Copy diagnostics
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F6F5]">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p
          className="text-[#6B7A73] text-sm font-medium"
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          Signing you in…
        </p>
      </div>
    </div>
  );
}
